import { useState, useEffect, useCallback, useMemo } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Card, Container, IconButton, Switch } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

// components
import Iconify from '../../../components/iconify';
import { ActionsPopover, DeleteModal, EmployeeFormModal, Header } from '.';
import Loading from '../../../layouts/loading';

// styles
import 'react-toastify/dist/ReactToastify.css';
import {
  getEmployee,
  createUser,
  updateUser,
  deleteUser,
  updateUserStatus,
  getUserRoles,
  updateUserMobileAccess,
} from '../../../services/employeeService';
import { checkComponentPermission } from '../../../utils/share';
import { boldHeader, USER_LIST_TABLE_COLUMNS, USER_LIST_TABLE_DEFAULT_PROPS } from '../constants';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import TableWrapper from '../../../common/TableWrapper';

// ----------------------------------------------------------------------

const canEditUser = checkComponentPermission('EDIT_USER');
const canDeleteUser = checkComponentPermission('DELETE_USER');
const showAction = canEditUser || canDeleteUser;

// ----------------------------------------------------------------------

function EmployeeTable() {
  
  const navigate = useNavigate();
  const userId = useSelector((state) => state?.login?.user.id || null);
  const [openActions, setOpenActions] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [action, setAction] = useState('');
  const [selectedRow, setSelectedRow] = useState([]);
  const [userRoleList, setUserRoleList] = useState([]);
  // const [isLoading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const USER_STATUS = ['Active', 'Inactive'];
  const {
    data: { count, rows } = { count: 0, rows: [] },
    isError,
    isRefetching,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting],
    queryFn: async () => {
      try {
        const response = await getEmployee({
          start: pagination.pageIndex * pagination.pageSize,
          pageSize: pagination.pageSize,
          filters: JSON.stringify(columnFilters ?? []),
          globalFilter,
          sorting: JSON.stringify(sorting ?? []),
        });
        
        return response?.data?.data;
      } catch (error) {
        toast.error('An error occurred while fetching the employee list. Please try again later.');
      }
      return { count: 0, rows: [] };
    },
    placeholderData: keepPreviousData,
  });

  const handleCreateClick = () => {
    setAction('create');
    setOpenFormModal(true);
  };

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
  }, []);

  const handleOpenMenu = (event, row) => {
    setOpenActions(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setOpenActions(null);
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setOpenActions(null);
    setOpenDelete(true);
  };

  const handleDeleteModalClose = () => setOpenDelete(false);

  const handleEditClick = (row) => {
    setSelectedRow(row);
    setOpenActions(null);
    setAction('update');
    setOpenFormModal(true);
  };

  const handleDelete = async () => {
    try {
      const response = await deleteUser(selectedRow?.id);
      toast.success(response?.data?.message);
      setOpenDelete(false);
      refetch();
    } catch (error) {
      toast.error('Sorry, an error occurred while deleting. Please try again later.');
    }
  };

  const handleFormModalSubmit = useCallback(
    async (data) => {
      try {
        const values = { ...data };
        values.userName = values?.userName?.trim();
        values.email = values?.email?.trim();
        let response;
        if (action === 'create') {
          response = await createUser(values);
        } else if (action === 'update') {
          delete values.email;
          response = await updateUser(selectedRow?.id, values);
        }

        refetch();
        toast.success(response?.data?.message);
        setOpenFormModal(false);
      } catch (error) {
        toast.error(`${data?.email?.trim()} is already exists`);
      }
    },
    [action, selectedRow?.id]
  );

  const handleToggleStatus = async (userId, currentStatus, refetch) => {
    const newStatus = currentStatus === 'active' ? 'deactivate' : 'activate';
    try {
      setIsUpdating(true);
      const response = await updateUserStatus(userId, newStatus);
      toast.success(response?.data?.message);
      refetch();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleMobileAccess = async (userId, currentAccess, refetch) => {
    const newAccess = !currentAccess;
    try {
      setIsUpdating(true);
      const response = await updateUserMobileAccess(userId, newAccess);
      toast.success(response?.data?.message);
      refetch();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    const fetchUserRoles = async () => {
      const userRoles = await getUserRoles();
      setUserRoleList(userRoles?.data?.data);
    };

    fetchUserRoles();
  }, []);

  const columns = useMemo(
    () => [
      ...USER_LIST_TABLE_COLUMNS,
      {
        accessorKey: 'canAccessMobile',
        header: 'Mobile Access',
        // filterVariant: 'select',
        // filterSelectOptions: USER_STATUS,
        enableColumnFilter: false,
        enableSorting: false,
        Header: boldHeader('#454F5B'),
        size: 30,
        Cell: ({ row }) => (
          <Switch
            checked={row.original.canAccessMobile}
            onClick={(e) => { e.stopPropagation(); }}
            onChange={() => handleToggleMobileAccess(row.original.id, row.original.canAccessMobile, refetch)}
            color="primary"
          />
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'select',
        filterSelectOptions: USER_STATUS,
        Header: boldHeader('#454F5B'),
        size: 30,
        Cell: ({ row }) => (
          <Switch
            checked={row.original.status === 'active'}
            onClick={(e) => { e.stopPropagation(); }}
            onChange={() => handleToggleStatus(row.original.id, row.original.status, refetch)}
            color="primary"
            disabled={userId === row.original.id && showAction}
          />
        ),
      },
      {
        id: 'actions',
        Header: 'Actions',
        size: 30,
        Cell: ({ row }) => (
          <div
            role="presentation"
            data-testid="action-buttons-container"
            style={{ display: 'flex', justifyContent: 'flex-start' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconButton
              disabled={userId === row.original.id && showAction}
              size="small"
              color="inherit"
              onClick={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                handleEditClick(row.original); 
              }}
            >
              <Iconify icon={'eva:edit-outline'} />
            </IconButton>
            <IconButton
              disabled={userId === row.original.id && showAction}
              size="small"
              onClick={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                e.nativeEvent.stopImmediatePropagation();
                handleDeleteClick(row.original); 
              }}
            >
              <Iconify
                icon={'eva:trash-2-outline'}
                sx={{
                  color: !(userId === row.original.id) && 'error.main',
                }}
              />
            </IconButton>
          </div>
        ),
      },
    ],
    [userId]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    muiToolbarAlertBannerProps: isError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      showSkeletons: isRefetching,
      showAlertBanner: isError,
    },
    rowCount: count ?? 0,
    muiTableBodyRowProps: ({ row }) => ({
      sx: { backgroundColor: '#fff', cursor: 'pointer' },
      onClick: (event) => {
        // Check if the click originated from an action button, switch, or their children
        const target = event.target;
        const isActionButton = target.closest('[role="button"]') || 
                              target.closest('.MuiIconButton-root') ||
                              target.closest('[data-testid*="action"]') ||
                              target.closest('[data-testid="action-buttons-container"]') ||
                              target.closest('button');
        
        // Check if the click originated from a Switch component
        const isSwitch = target.closest('.MuiSwitch-root') ||
                        target.closest('[role="switch"]') ||
                        target.closest('input[type="checkbox"]');
        
        // Don't navigate if clicking on action buttons or switches
        if (isActionButton || isSwitch) {
          return;
        }
        
        const id = row?.original?.id;
        if (id) navigate(`/employee/profile/${id}`);
      },
    }),
    ...USER_LIST_TABLE_DEFAULT_PROPS,
  });

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Users',
      path: '',
    },
  ];

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="4xl">
        <Loading open={isLoading || isUpdating} />

        <Header onCreateClick={handleCreateClick} />

        <Card>
          <Card>
            <TableWrapper>
              <MaterialReactTable table={table} />
            </TableWrapper>
          </Card>
        </Card>
      </Container>
      <ActionsPopover
        open={openActions}
        onClose={handleCloseMenu}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />

      {openFormModal && (
        <EmployeeFormModal
          open
          onClose={handleFormModalClose}
          selectedRow={selectedRow}
          action={action}
          onSubmit={handleFormModalSubmit}
          userRoleList={userRoleList}
        />
      )}
      <DeleteModal open={openDelete} onClose={handleDeleteModalClose} onDelete={handleDelete} />
    </>
  );
}

export default EmployeeTable;
